/**
 * xlsx evidence workbook (MERGE-PLAN §4.4) — a control-by-control matrix plus a
 * per-framework coverage sheet. Built from raw OOXML + the store-only zip writer,
 * so there is no runtime CDN and no new npm dependency (zero-egress preserved).
 *
 * Uses inline strings (t="inlineStr") to avoid a shared-strings table.
 */
import { FRAMEWORK_LABELS, type FrameworkId } from '../../types';
import type { EvidencePack } from '../types';
import { dateStamp, downloadBlob, slugify } from './download';
import { buildZip, type ZipFile } from './zip';

const FRAMEWORK_COLUMNS: readonly FrameworkId[] = [
  'cis',
  'nist',
  'iso',
  'soc2',
  'hipaa',
  'secScore',
];

function xmlEsc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function colLetter(index0: number): string {
  let n = index0;
  let s = '';
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

function sheetXml(rows: string[][]): string {
  const rowsXml = rows
    .map((cells, r) => {
      const cellsXml = cells
        .map((val, c) => {
          const ref = `${colLetter(c)}${r + 1}`;
          return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEsc(val)}</t></is></c>`;
        })
        .join('');
      return `<row r="${r + 1}">${cellsXml}</row>`;
    })
    .join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${rowsXml}</sheetData></worksheet>`;
}

function evidenceRows(pack: EvidencePack): string[][] {
  const header = [
    'Control ID',
    'Objective',
    'Module',
    'Severity',
    'Status',
    'Affected count',
    'Affected objects',
    'Endpoints',
    'Collected at',
    ...FRAMEWORK_COLUMNS.map((f) => FRAMEWORK_LABELS[f]),
    'Remediation',
  ];
  const body = pack.records.map((rec) => [
    rec.controlId,
    rec.objective,
    rec.module,
    rec.severity,
    rec.status,
    String(rec.affected.length),
    rec.affected.map((a) => `${a.label}${a.detail ? ` (${a.detail})` : ''}`).join('; '),
    rec.provenance.endpoints.join('; '),
    rec.provenance.collectedAt,
    ...FRAMEWORK_COLUMNS.map((f) => rec.frameworks[f].join(' ')),
    rec.remediation,
  ]);
  return [header, ...body];
}

function coverageRows(pack: EvidencePack): string[][] {
  const header = ['Framework', 'Controls tested', 'Passed', 'Failed', 'Coverage %'];
  const body = pack.coverage.map((cov) => [
    FRAMEWORK_LABELS[cov.framework],
    String(cov.total),
    String(cov.passed),
    String(cov.failed),
    `${cov.score}%`,
  ]);
  const summary = [
    ['', '', '', '', ''],
    ['Tenant', pack.meta.tenantName, '', '', ''],
    ['Collected by', pack.meta.collectedBy, '', '', ''],
    ['Collected at', pack.meta.collectedAt, '', '', ''],
    ['Posture score', `${pack.posture.score}/100`, `Grade ${pack.posture.grade}`, '', ''],
  ];
  return [header, ...body, ...summary];
}

export function buildEvidenceXlsx(pack: EvidencePack): Uint8Array {
  const files: ZipFile[] = [
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Evidence" sheetId="1" r:id="rId1"/><sheet name="Coverage" sheetId="2" r:id="rId2"/></sheets></workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/></Relationships>`,
    },
    { name: 'xl/worksheets/sheet1.xml', content: sheetXml(evidenceRows(pack)) },
    { name: 'xl/worksheets/sheet2.xml', content: sheetXml(coverageRows(pack)) },
  ];
  return buildZip(files);
}

export function downloadEvidenceXlsx(pack: EvidencePack): void {
  const name = `evidence-${slugify(pack.meta.tenantName)}-${dateStamp(pack.meta.collectedAt)}.xlsx`;
  const bytes = buildEvidenceXlsx(pack);
  downloadBlob(
    name,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    bytes,
  );
}
